/**
 * Gateway Initializer
 * Builds and starts the Gateway service with the authentication the boot gate
 * settled on.
 *
 * Health/connection state changes are managed through useGatewayStore.
 * Components subscribe to store changes to react to updates.
 */

import GatewayService from './gateway-service';
import Typo3AuthService from './typo3-auth-service';
import { logger } from './logger';

export interface GatewayInitializerOptions {
  /** Pre-validated auth service from the boot gate; null for non-proxy/dev. */
  authService?: Typo3AuthService | null;
  getApiKey?: () => string | null;
  handleNoApiKey?: () => void;
}

export class GatewayInitializer {
  private authService: Typo3AuthService | null;
  private getApiKey?: () => string | null;
  private handleNoApiKey?: () => void;

  constructor(options: GatewayInitializerOptions) {
    this.authService = options.authService ?? null;
    this.getApiKey = options.getApiKey;
    this.handleNoApiKey = options.handleNoApiKey;
  }

  /**
   * Build the Gateway service for this boot: proxy mode when the boot gate
   * validated a TYPO3 session, direct mode otherwise. Called right after the
   * gate, before any component holds the service, so nothing has to be
   * rewired later (#132).
   */
  create(): GatewayService {
    if (!this.authService) {
      return new GatewayService();
    }
    const typo3BaseUrl = import.meta.env.VITE_TYPO3_BASE_URL as string;
    return new GatewayService({
      useProxy: true,
      proxyUrl: `${typo3BaseUrl}/index.php?type=3002`,
      authService: this.authService, // ✅ SECURITY: Pass auth service for CSRF tokens
    });
  }

  /**
   * Initialize the service and start health polling (proxy mode polls through
   * the proxy too; the first poll sets health).
   * @returns false when there is neither a TYPO3 session nor an API key
   */
  start(gateway: GatewayService): boolean {
    try {
      if (this.authService) {
        gateway.initializeProxy();
        logger.info('TYPO3 session pre-validated - Gateway in proxy mode');
        gateway.startPolling(30000);
        return true;
      }

      const apiKey = this.getApiKey ? this.getApiKey() : null;
      if (apiKey) {
        logger.info('Initializing Gateway service with API key...');
        gateway.initialize(apiKey);
        gateway.startPolling(30000);
        return true;
      }

      this.handleNoApiKey?.();
      return false;
    } catch (error) {
      logger.error('Failed to initialize Gateway service:', error);
      return false;
    }
  }
}
